/**
 * The part of the document the editor does not hold, rendered as plain html by the doc-engine.
 *
 * Two instances, above and below the editor. React owns the container; the elements inside it are placed
 * imperatively, because the DOM has to change in the SAME task the editor is hidden or revealed in -
 * React's render is a task later, long enough for the page to be one part short and slide.
 */
import {
  DragEvent,
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import classNames from 'classnames';

import { SignInContext } from '@modules/users';

import DocumentContext from '../../contexts/DocumentContext.context';
import { isVersionBound, isVersionDetached } from '../../helpers/isVersionBound.helper';
import { resolveStaticContentLinkTarget } from '../../helpers/scrollContent/staticContentLinkTarget';
import useScrollContent from '../../hooks/useScrollContent.hook';
import { getImageFilesFromFileList } from '../../tiptap/utils/imageTransfer';
import { insertImages } from '../../tiptap/utils/imageInsert';
import { DOCUMENT_HTML_ATTRIBUTES } from '../../constants';
import type { TElement } from '../model/elements';
import {
  activateElementId,
  elementList,
  getElementsSnapshot,
  isSwapping,
  registerStaticSync,
  subscribeElements,
  whenSwapDone,
} from '../editor/controller';
import {
  ELEMENT_ID_SELECTOR,
  LARGE_DOCUMENT_STATIC_CLASS,
  ownIdOf,
  STATIC_ELEMENT_ID_ATTR,
  STATIC_ELEMENT_INDEX_ATTR,
} from '../constants';
import { anchorOffsetTop, correctActiveHold } from '../editor/anchorHold';
import type { TPoint } from '../editor/caretRestore';
import { liveEditorView } from '../editor/liveEditor';
import { measureAndSkip } from './staticMeasure';
import { buildNodes, buildStats, bumpRenderVersion, evictOnCommit, parsedElements, place } from './staticNodes';
import { hasStaticRenderContext, subscribeStaticRender } from './staticRenderCache';
import { cellUnder, trace } from '../trace';

type TProps = {
  block: 'above' | 'below';
  /** Taken from the editor's own wrapper, so both halves resolve `.document-content` rules alike. */
  landscape: boolean;
};

const containers: Record<TProps['block'], HTMLDivElement | null> = { above: null, below: null };

let syncs = 0;
let measureQueued = false;

const measurePass = () => {
  let more = false;
  if (containers.above) more = measureAndSkip(containers.above) || more;
  if (containers.below) more = measureAndSkip(containers.below) || more;
  if (!more || measureQueued) return;
  measureQueued = true;
  const runner = window.requestIdleCallback ?? window.requestAnimationFrame;
  runner(() => {
    measureQueued = false;
    measurePass();
  });
};

subscribeStaticRender(bumpRenderVersion);

const syncStaticElements = () => {
  if (!hasStaticRenderContext()) return;
  const started = performance.now();
  const parsedBefore = parsedElements;
  evictOnCommit(elementList());
  const evicted = performance.now();
  const snapshot = getElementsSnapshot();
  // Absolute positions, so an element's key does not change when the live part moves.
  const total = elementList()?.length ?? snapshot.elementsAbove.length + snapshot.elementsBelow.length;
  buildStats.hits = 0;
  buildStats.misses = 0;
  buildStats.reused = 0;
  const all = elementList() ?? [];
  const above = containers.above ? buildNodes(snapshot.elementsAbove, 0, 'above', all) : null;
  const below = containers.below
    ? buildNodes(snapshot.elementsBelow, total - snapshot.elementsBelow.length, 'below', all)
    : null;
  const built = performance.now();
  if (containers.above && above) place(containers.above, above);
  if (containers.below && below) place(containers.below, below);
  const placed = performance.now();
  measurePass();
  // This sync just changed the page's height. Anything being held in place is put back in the SAME
  // task, so the movement and its correction paint together instead of a frame or more apart.
  correctActiveHold();
  syncs++;
  const ms = Math.round(performance.now() - started);
  // Only the expensive ones: a sync that reused every node is the normal case and says nothing.
  if (ms > 8 || parsedElements - parsedBefore > 0) {
    trace('static:sync', {
      ms,
      sync: syncs,
      above: snapshot.elementsAbove.length,
      below: snapshot.elementsBelow.length,
      elementsReparsed: parsedElements - parsedBefore,
      evictMs: Math.round(evicted - started),
      buildMs: Math.round(built - evicted),
      hits: buildStats.hits,
      misses: buildStats.misses,
      reused: buildStats.reused,
      placeMs: Math.round(placed - built),
      measureMs: Math.round(performance.now() - placed),
    });
  }
};

registerStaticSync(syncStaticElements);

/** Re-place as more elements finish rendering. */
const useStaticRenderTick = () => {
  const [, setTick] = useState(0);
  useEffect(() => subscribeStaticRender(() => setTick((tick) => tick + 1)), []);
};

// The index decides the part; the id, only used for the scroll anchor, walks back to the nearest element that has one.
const elementAt = (container: HTMLElement, target: Node): { index: number; id: string | null } | null => {
  // Up to the nearest element carrying a position, not up to the container's own child: a run of
  // section content sits inside a `<section>`, so the container's child is that wrapper, which has none.
  let node: Node | null = target;
  while (node && node !== container && !(node instanceof Element && node.hasAttribute(STATIC_ELEMENT_INDEX_ATTR))) {
    node = node.parentNode;
  }
  const placed = node instanceof Element && node.hasAttribute(STATIC_ELEMENT_INDEX_ATTR) ? node : null;
  const index = Number(placed?.getAttribute(STATIC_ELEMENT_INDEX_ATTR) ?? NaN);
  if (!Number.isInteger(index)) return null;
  let element: Element | null = placed;
  while (element) {
    const id = element.getAttribute(STATIC_ELEMENT_ID_ATTR);
    if (id) return { index, id };
    element = element.previousElementSibling;
  }
  return { index, id: null };
};

const StaticElements = ({ block, landscape }: TProps) => {
  const { version, refContentNode } = useContext(DocumentContext);
  const { organization } = useContext(SignInContext);
  const scrollContent = useScrollContent();
  // The value is read by `syncStaticElements`; subscribing here is what makes a model change re-render.
  useSyncExternalStore(subscribeElements, getElementsSnapshot);
  useStaticRenderTick();
  const ref = useRef<HTMLDivElement>(null);

  // No dependency list: every render re-places, so a React-driven update (an element finished
  // rendering) and a controller-driven one (a swap) converge on the same DOM.
  useEffect(() => {
    containers[block] = ref.current;
    syncStaticElements();
    return () => {
      containers[block] = null;
    };
  });

  /** Where the pointer went down: a click and a drag arrive as the same events, so both ends are needed. */
  const pressedAt = useRef<TPoint | null>(null);

  /** True when the click resolved to an element and a swap was asked for. */
  const activateAt = useCallback((event: MouseEvent<HTMLDivElement>, after?: () => void): boolean => {
    const container = ref.current;
    if (!container || !(event.target instanceof Node)) return false;

    const down = pressedAt.current;
    const downTarget = down ? document.elementFromPoint(down.x, down.y) : null;
    const hit =
      (downTarget && container.contains(downTarget) ? elementAt(container, downTarget) : null) ??
      elementAt(container, event.target);
    if (!hit) {
      trace('static:click-unresolved', { block, target: (event.target as Element).nodeName });
      return false;
    }
    // Only for the anchor. The part comes from `hit.index` - see `elementAt`.
    const elementId = hit.id;
    // The paper's own handler appends a trailing paragraph on "click below the content".
    event.stopPropagation();
    const scroller = refContentNode.current?.view;
    const offsetTop = scroller && elementId ? anchorOffsetTop(scroller, elementId) : null;
    const anchor = scroller && offsetTop !== null ? { scroller, offsetTop, scrollTop: scroller.scrollTop } : undefined;
    // The drag's start point is the selection's anchor, so it doubles as the caret point.
    const start = pressedAt.current;
    const dragged =
      start && (Math.abs(start.x - event.clientX) > 3 || Math.abs(start.y - event.clientY) > 3) ? start : null;
    const selected = !window.getSelection()?.isCollapsed;
    const clickPoint = dragged && selected ? dragged : { x: event.clientX, y: event.clientY };
    const selectionEnd = dragged && selected ? { x: event.clientX, y: event.clientY } : undefined;
    const aimId = ownIdOf(document.elementFromPoint(clickPoint.x, clickPoint.y)?.closest(ELEMENT_ID_SELECTOR) ?? null);
    trace('static:click', {
      block,
      elementId,
      clickPoint,
      selectionEnd,
      // Beside `elementId`: the aim follows `aimId`, so the two disagreeing is what a lost caret looks like.
      aimId,
      anchorFound: Boolean(anchor),
      button: event.button,
      // The caret log records the same shape from the editor afterwards, so the two can be compared.
      aimedAt: cellUnder(clickPoint.x, clickPoint.y),
    });
    const activated = activateElementId(elementId ?? '', {
      clickPoint,
      selectionEnd,
      anchor,
      elementIndex: hit.index,
      aimId,
    });
    // The running swap is loading a different part, so acting when it finishes would act on the wrong content.
    if (!activated && isSwapping()) {
      trace('static:click-during-swap', { block, elementId });
      return false;
    }
    if (after) whenSwapDone(after);
    return true;
  }, []);

  /**
   * A file dropped here is the browser's to handle unless something says otherwise, and what it does
   * is replace the document with the file. Part loading turned that from a narrow edge (the paper
   * margins) into most of the visible page. The tab navigating away also leaves the server-side edit
   * lock held, so coming back lands in the "editing in another tab" dialog.
   *
   * Only for drags that carry files: a citation dragged from Insights still has no handler here, and
   * showing it a drop cursor would promise something that does not happen.
   */
  const onDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    if (Array.from(event.dataTransfer?.types ?? []).includes('Files')) event.preventDefault();
  }, []);

  const onDrop = useCallback((event: DragEvent<HTMLDivElement>) => {
    if (!Array.from(event.dataTransfer?.types ?? []).includes('Files')) return;
    event.preventDefault();
    // Read now, not in the callback: the DataTransfer is emptied once this handler returns.
    const files = getImageFilesFromFileList(event.dataTransfer?.files);
    const point = { x: event.clientX, y: event.clientY };
    // A non-image file is already prevented from navigating; there is nothing to insert.
    if (!files.length) return;
    // No pointer went down here, so the drag-selection test below must not read a stale press.
    pressedAt.current = null;
    activateAt(event, () => {
      // Never the captured `editor`: this callback was memoized on the first render, and entering
      // edit mode replaces the editor instance.
      const view = liveEditorView();
      if (!view) return;
      const at = view.posAtCoords({ left: point.x, top: point.y });
      insertImages({ view, files, insertPos: at?.pos ?? view.state.selection.from, eventMeta: 'drop' });
    });
  }, []);

  const onClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    // Followed natively, the link lands on the wrong copy of the id, or at the top when the target is in another part.
    // The box flips itself before any handler runs and nothing here saves it, so the editor applies the tick instead.
    if (event.target instanceof HTMLInputElement && event.target.type === 'checkbox') {
      event.preventDefault();
      /**
       * Click the real one rather than reproducing what a tick does.
       *
       * A checkbox is legal in a lot of places - the body, a table cell, a template field, a reusable
       * block - and each nesting has its own node structure. Reaching into the document to set an
       * attribute means getting all of them right, and getting them right again whenever a new
       * nesting becomes legal. The editor already handles every one of them, so hand it a real click
       * and let it decide what that means.
       *
       * Found by the task item's OWN id - not by screen coordinates, which the anchor hold keeps
       * correcting for 2.5s after a swap, and not by the element, because `data-element-id` is stamped
       * only on elements that have an id of their own. Plenty do not: a bare page break, and a comment
       * selection, which wraps its content in a `selection-node`. A checkbox inside one of those had
       * no element to be found within, so the tick silently did nothing.
       */
      const itemId = event.target.closest('[data-id]')?.getAttribute('data-id') ?? null;
      const activated = activateAt(event);
      trace('static:checkbox', { activated, itemId });
      if (!activated || !itemId) return;
      whenSwapDone(() => {
        const dom = liveEditorView()?.dom as HTMLElement | undefined;
        const item = dom?.querySelector(`[data-id="${CSS.escape(itemId)}"]`);
        const box = item?.querySelector('input[type="checkbox"]');
        trace('static:checkbox-apply', { itemId, foundItem: Boolean(item), foundBox: box instanceof HTMLInputElement });
        if (box instanceof HTMLInputElement) box.click();
      });
      return;
    }

    const element = event.target instanceof Element ? event.target : (event.target as Node).parentElement;
    const target = resolveStaticContentLinkTarget(element);
    if (target) {
      event.preventDefault();
      event.stopPropagation();
      trace('static:link', { block, targetId: target.value });
      scrollContent({ value: target.value, attribute: DOCUMENT_HTML_ATTRIBUTES.CUSTOM_UNIQUE_ID });
      return;
    }

    activateAt(event);
  }, []);

  // The browser's own menu would describe static html that is about to be replaced.
  const onContextMenu = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const { clientX, clientY } = event;
    const handled = activateAt(event, () => {
      const target = document.elementFromPoint(clientX, clientY);
      target?.dispatchEvent(
        new window.MouseEvent('contextmenu', { bubbles: true, cancelable: true, clientX, clientY, button: 2 })
      );
    });
    if (handled) event.preventDefault();
  }, []);

  return (
    <div
      ref={ref}
      onMouseDown={(event) => {
        pressedAt.current = { x: event.clientX, y: event.clientY };
      }}
      onClick={onClick}
      onContextMenu={onContextMenu}
      onDragOver={onDragOver}
      onDrop={onDrop}
      translate="no"
      // Same container classes as the changes view, so every rule already written for statically
      // rendered document content applies here instead of being re-invented.
      className={classNames(
        'notranslate document-content static-content',
        LARGE_DOCUMENT_STATIC_CLASS,
        `custom-${organization.stylesConfig.styleVariant}`,
        { 'landscape-mode': landscape },
        { 'template-detached': isVersionDetached(version) },
        { 'template-bound': isVersionBound(version) }
      )}
      // Both containers stay mounted even while empty, so a swap can fill one in the same task it
      // hides the editor. Named, because two identically-classed containers is a trap for anything
      // that measures them.
      data-large-document-block={block}
    />
  );
};

export default StaticElements;
